import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { instanceToPlain, plainToInstance } from "class-transformer";
import { Collections, db, functions } from "../../utils/firebase.config";
import { converterOf } from "../../utils/firebase.utils";
import EntityMarket, { MarketType } from "../../entities/market.entity";
import FetchError from "../../errors/fetch.error";
import CreateMarketResource from "./resources/create-market.resource";
import UpdateMarketResource from "./resources/update-market.resource";
import MarketResource from "./resources/market.resources";

export function useMarkets() {
  const marketsCollection = collection(db, Collections.Market).withConverter(converterOf(EntityMarket));

  const fetchAll = async (): Promise<EntityMarket[]> => {
    const response = await getDocs<EntityMarket>(marketsCollection);
    return response.docs.map((market) => market.data());
  };

  const fetch = async (id: string): Promise<EntityMarket> => {
    const marketDoc = doc(marketsCollection, id);

    const resource = await getDoc(marketDoc);

    const data = resource.data();

    if (!data) {
      throw new FetchError({ trace: 'fetch', message: `Não foi possível carregar o mercado com id: ${id}`});
    }

    return data;
  };

  const create = async (market: Omit<EntityMarket, 'id'>): Promise<EntityMarket> => {
    const insertMarket = httpsCallable<unknown, MarketResource>(functions, 'insertMarket');

    const newMarket = new CreateMarketResource({
      ...market,
      prestashopOrderStatus: {
        prestashopCanceledStateId: market.prestashopCanceledStateId,
        prestashopPaidStateId: market.prestashopPaidStateId,
        prestashopPaymentFailedStateId: market.prestashopPaymentFailedStateId,
        prestashopWaitingPaymentStateId: market.prestashopWaitingPaymentStateId
      },
      marketCode: market.marketCode?.toUpperCase(),
    });

    const { data } = await insertMarket(instanceToPlain(newMarket));

    return new EntityMarket({
      ...data,
      prestashopCanceledStateId: data.prestashopOrderStatus?.prestashopCanceledStateId,
      prestashopPaidStateId: data.prestashopOrderStatus?.prestashopPaidStateId,
      prestashopPaymentFailedStateId: data.prestashopOrderStatus?.prestashopPaymentFailedStateId,
      prestashopWaitingPaymentStateId: data.prestashopOrderStatus?.prestashopWaitingPaymentStateId
    });
  };

  const update = async (market: Omit<Partial<EntityMarket>, 'id' | 'type'> & { id: string, type: MarketType }): Promise<void> => {
    const updateMarket = httpsCallable(functions, 'updateMarket');

    const instance = new UpdateMarketResource({
      ...market,
      prestashopOrderStatus: {
        prestashopCanceledStateId: market.prestashopCanceledStateId,
        prestashopPaidStateId: market.prestashopPaidStateId,
        prestashopPaymentFailedStateId: market.prestashopPaymentFailedStateId,
        prestashopWaitingPaymentStateId: market.prestashopWaitingPaymentStateId
      },
      marketCode: market.marketCode?.toUpperCase(),
    });

    await updateMarket(instanceToPlain(instance));
  };

  return { fetchAll, fetch, create, update };
}

export default useMarkets;
